import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { ImportFixes } from '@/components/migration/import-fixes';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DrillLink } from '@/features/reports/report-table';
import type { Drill } from '@/features/reports/types';
import { useAccounts, useTaxCodes } from '@/lib/accounts';
import { api } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { fmtAmount, isZero, useMigration } from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import {
  MODE_LABEL,
  PHASE_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  checkBody,
  checkErrors,
  isActive,
  mappingBody,
  progressOf,
  useImportRun,
  useImportRuns,
  useVerification,
  type CheckForm,
  type ImportPlan,
  type RunView,
} from '@/lib/qbo-history';
import { useQbo } from '@/lib/qbo';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/transactions')({
  validateSearch: (s: Record<string, unknown>): { run?: string } =>
    typeof s['run'] === 'string' ? { run: s['run'] } : {},
  component: ImportTransactions,
});

/**
 * Screen G12 (document 06): Import transactions from QuickBooks Online. Choose the range and
 * mode, check (dry run), map, start, watch the progress, then compare with QuickBooks.
 */
function ImportTransactions() {
  const orgId = useOrgId();
  const { run: runId } = Route.useSearch();
  const runs = useImportRuns();
  const qbo = useQbo();
  // the latest run unless one is chosen in the history
  const id = runId ?? runs.data?.find((r) => r.status !== 'cancelled')?.id;
  return (
    <>
      <PageHeader
        title="Import transactions"
        description="Bring the client's invoices, bills, payments, expenses, deposits, transfers and journal entries over from QuickBooks Online as Yedo Books transactions."
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration/history">QuickBooks import history</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : qbo.data && !qbo.data.connection ? (
        <EmptyState
          title="Connect QuickBooks first"
          hint="Connect the client's QuickBooks Online company on Move from QuickBooks."
        />
      ) : runs.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="flex max-w-6xl flex-col gap-4">
          <CheckCard key={orgId} />
          {id ? <RunPanel id={id} /> : null}
        </div>
      )}
    </>
  );
}

function CheckCard() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const m = useMigration();
  const conversion = m.data?.conversionDate ?? null;
  const [f, setF] = useState<CheckForm>({
    mode: 'full_history',
    from: '',
    to: '',
    estimates: true,
    allowExisting: false,
  });
  const [touched, setTouched] = useState(false);
  const errors = touched ? checkErrors(f) : {};
  const check = useMutation({
    mutationFn: () =>
      api<RunView>(`/orgs/${orgId}/qbo/history`, {
        method: 'POST',
        body: JSON.stringify(checkBody(f)),
      }),
    onSuccess: (v) => {
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
      void navigate({ to: '/settings/migration/transactions', search: { run: v.id } });
    },
  });
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-4"
      aria-labelledby="check-title"
      data-testid="qbo-import-check"
    >
      <h2 id="check-title" className="font-medium">
        What to import
      </h2>
      <ErrorBanner>{check.error?.message}</ErrorBanner>
      <form
        className="grid gap-3 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (Object.keys(checkErrors(f)).length === 0) check.mutate();
        }}
      >
        <Field
          label="Import"
          hint={
            f.mode === 'after_conversion'
              ? conversion
                ? `From the conversion date ${formatDate(conversion)}, on top of the opening balances.`
                : 'Post the opening balances on Move from QuickBooks first.'
              : 'Every transaction from the start date; earlier balances come over as opening balances.'
          }
        >
          {(id) => (
            <NativeSelect
              id={id}
              value={f.mode}
              onChange={(e) => setF({ ...f, mode: e.target.value as CheckForm['mode'] })}
            >
              <option value="full_history">{MODE_LABEL.full_history}</option>
              <option value="after_conversion">{MODE_LABEL.after_conversion}</option>
            </NativeSelect>
          )}
        </Field>
        {f.mode === 'full_history' ? (
          <Field label="From" error={errors.from}>
            {(id, d) => (
              <DateInput
                id={id}
                aria-describedby={d}
                value={f.from}
                invalid={Boolean(errors.from)}
                onChange={(from) => setF({ ...f, from })}
              />
            )}
          </Field>
        ) : (
          <div />
        )}
        <Field label="To" error={errors.to}>
          {(id, d) => (
            <DateInput
              id={id}
              aria-describedby={d}
              value={f.to}
              invalid={Boolean(errors.to)}
              onChange={(to) => setF({ ...f, to })}
            />
          )}
        </Field>
        <div className="flex flex-col justify-end gap-2">
          <Checkbox
            label="Estimates as quotes"
            checked={f.estimates}
            onChange={(e) => setF({ ...f, estimates: e.target.checked })}
          />
          <Checkbox
            label="Import anyway if Ledger already has transactions in the range"
            checked={f.allowExisting}
            onChange={(e) => setF({ ...f, allowExisting: e.target.checked })}
          />
        </div>
        <div className="sm:col-span-4">
          <Button type="submit" disabled={check.isPending}>
            {check.isPending ? 'Checking…' : 'Check'}
          </Button>
          <span className="ml-3 text-xs text-muted-foreground">
            Nothing is posted until you start the import.
          </span>
        </div>
      </form>
    </section>
  );
}

function RunPanel({ id }: { id: string }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const q = useImportRun(id);
  const act = useMutation({
    mutationFn: (what: 'start' | 'cancel' | 'resume') =>
      api<RunView>(`/orgs/${orgId}/qbo/history/${id}/${what}`, { method: 'POST' }),
    onSuccess: (v) => {
      qc.setQueryData(['qbo-history', orgId, id], v);
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
    },
  });
  if (q.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <ErrorBanner>{q.error.message}</ErrorBanner>;
  const run = q.data;
  return (
    <section
      className="flex flex-col gap-4 rounded-lg border bg-card px-4 py-4"
      aria-labelledby="run-title"
      data-testid="qbo-import-run"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 id="run-title" className="font-medium">
              {MODE_LABEL[run.mode]}: {formatDate(run.from)} – {formatDate(run.to)}
            </h2>
            <StatusChip tone={STATUS_TONE[run.status]}>{STATUS_LABEL[run.status]}</StatusChip>
          </div>
          <p className="text-xs text-muted-foreground">
            {run.plan?.company.name ? `${run.plan.company.name} · ` : ''}
            {run.startedByName ? `by ${run.startedByName} · ` : ''}checked{' '}
            {formatDateTime(run.createdAt)}
            {run.finishedAt ? ` · finished ${formatDateTime(run.finishedAt)}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {run.status === 'planned' ? (
            <Button disabled={!run.canStart || act.isPending} onClick={() => act.mutate('start')}>
              Start import
            </Button>
          ) : null}
          {run.status === 'failed' ? (
            <Button disabled={act.isPending} onClick={() => act.mutate('resume')}>
              Resume
            </Button>
          ) : null}
          {['planning', 'planned', 'queued', 'running', 'failed'].includes(run.status) ? (
            <Button
              variant="outline"
              disabled={act.isPending || run.cancelRequested}
              onClick={() => act.mutate('cancel')}
            >
              {run.cancelRequested ? 'Cancelling…' : 'Cancel'}
            </Button>
          ) : null}
        </div>
      </div>
      <ErrorBanner>{act.error?.message}</ErrorBanner>
      {run.lastError ? <ErrorBanner>{run.lastError}</ErrorBanner> : null}
      {run.status === 'planning' ? (
        <p className="text-sm text-muted-foreground" role="status">
          Reading QuickBooks and checking every transaction…
        </p>
      ) : null}
      {isActive(run.status) && run.status !== 'planning' ? <Progress run={run} /> : null}
      {run.status === 'completed' ? <Result run={run} /> : null}
      {run.plan ? <Plan run={run} plan={run.plan} /> : null}
      {run.fixGroups.length ? <ImportFixes run={run} /> : null}
      {run.issues.length ? <Issues run={run} /> : null}
      {run.status === 'completed' ? <Verification id={run.id} /> : null}
    </section>
  );
}

function Progress({ run }: { run: RunView }) {
  const p = progressOf(run.progress.types);
  return (
    <div className="flex flex-col gap-2" role="status" aria-live="polite">
      <p className="text-sm">
        {PHASE_LABEL[run.progress.phase ?? 'reading'] ?? 'Working'}
        {run.progress.phase === 'importing' ? ` · ${p.done} of ${p.total}` : ''}
      </p>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${p.percent}%` }} />
      </div>
      <ul className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
        {Object.entries(run.progress.types ?? {}).map(([type, t]) => (
          <li key={type}>
            {run.plan?.types.find((x) => x.type === type)?.label ?? type}:{' '}
            {t.done + t.skipped + t.failed}/{t.total}
            {t.failed ? ` · ${t.failed} failed` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Result({ run }: { run: RunView }) {
  return (
    <p className="text-sm" data-testid="qbo-import-result">
      <strong>{run.imported}</strong> transactions imported, {run.skipped} skipped (voided or zero
      in QuickBooks)
      {run.failed ? (
        <>
          , <strong className="text-destructive">{run.failed} could not be imported</strong> (see
          the problems below)
        </>
      ) : null}
      .{' '}
      <Link
        className="underline"
        to="/settings/migration/history/$runId"
        params={{ runId: run.id }}
      >
        See what was imported
      </Link>
    </p>
  );
}

function Plan({ run, plan }: { run: RunView; plan: ImportPlan }) {
  return (
    <div className="flex flex-col gap-4">
      {plan.blocking.length ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm"
          role="alert"
          data-testid="qbo-import-blocking"
        >
          <p className="font-medium">The import cannot start yet:</p>
          <ul className="ml-5 list-disc">
            {plan.blocking.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">In QuickBooks</TableHead>
            <TableHead className="text-right">To import</TableHead>
            <TableHead className="text-right">Already imported</TableHead>
            <TableHead className="text-right">Skipped</TableHead>
            <TableHead className="text-right">Problems</TableHead>
            <TableHead className="text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plan.types
            .filter((t) => t.count)
            .map((t) => (
              <TableRow key={t.type}>
                <TableCell>{t.label}</TableCell>
                <TableCell className="tnum text-right">{t.count}</TableCell>
                <TableCell className="tnum text-right">{t.toImport}</TableCell>
                <TableCell className="tnum text-right">{t.already}</TableCell>
                <TableCell className="tnum text-right">{t.skipped}</TableCell>
                <TableCell
                  className={cn('tnum text-right', t.errors && 'font-medium text-destructive')}
                >
                  {t.errors}
                </TableCell>
                <TableCell className="tnum text-right">{fmtAmount(t.total)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {plan.opening ? (
        <p className="text-sm">
          QuickBooks has balances before {formatDate(run.from)}: {plan.opening.lines} trial balance
          lines come over as opening balances at {formatDate(plan.opening.asOf)}, with open invoices
          ({fmtAmount(plan.opening.receivable)}) and unpaid bills ({fmtAmount(plan.opening.payable)}
          ).
        </p>
      ) : null}
      {run.status === 'planned' ? <Mapping run={run} plan={plan} /> : null}
    </div>
  );
}

function Mapping({ run, plan }: { run: RunView; plan: ImportPlan }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const accounts = useAccounts();
  // codes no longer in force too: older QuickBooks documents keep their rate (GST 12%, 28%)
  const codes = useTaxCodes({ includeInactive: true });
  const [acct, setAcct] = useState<Record<string, string>>({});
  const [tax, setTax] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: async () => {
      await api(`/orgs/${orgId}/qbo/history/mapping`, {
        method: 'PUT',
        body: JSON.stringify(mappingBody(plan.mapping, { accounts: acct, taxCodes: tax })),
      });
      // check again with the new mapping
      return api<RunView>(`/orgs/${orgId}/qbo/history`, {
        method: 'POST',
        body: JSON.stringify({
          mode: run.mode,
          ...(run.mode === 'full_history' ? { from: run.from } : {}),
          to: run.to,
          options: run.options,
        }),
      });
    },
    onSuccess: (v) => {
      toast('Mapping saved; checking again');
      void qc.invalidateQueries({ queryKey: ['qbo-history', orgId] });
      void navigate({ to: '/settings/migration/transactions', search: { run: v.id } });
    },
  });
  const toCreate = plan.mapping.contacts.filter((c) => c.how === 'create');
  const itemsToCreate = plan.mapping.items.filter((i) => i.how === 'create');
  const usable = (accounts.data ?? []).filter((a) => a.isActive);
  return (
    <details
      className="rounded-md border px-3 py-2"
      open={plan.blocking.length > 0}
      data-testid="qbo-import-mapping"
    >
      <summary className="cursor-pointer text-sm font-medium">
        Mapping: {plan.mapping.accounts.length} accounts, {plan.mapping.taxCodes.length} tax codes,{' '}
        {toCreate.length} customers and vendors and {itemsToCreate.length} products to add
      </summary>
      <div className="mt-3 flex flex-col gap-4">
        <ErrorBanner>{save.error?.message}</ErrorBanner>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>QuickBooks account</TableHead>
              <TableHead className="text-right">Used</TableHead>
              <TableHead>Yedo Books account</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.mapping.accounts.map((a) => (
              <TableRow
                key={a.qboId}
                className={cn(!a.accountId && !acct[a.qboId] && 'bg-warning/10')}
              >
                <TableCell>
                  {a.name}
                  {a.type ? <div className="text-xs text-muted-foreground">{a.type}</div> : null}
                </TableCell>
                <TableCell className="tnum text-right">{a.used}</TableCell>
                <TableCell>
                  <NativeSelect
                    aria-label={`Ledger account for ${a.name}`}
                    value={acct[a.qboId] ?? a.accountId ?? ''}
                    onChange={(e) => setAcct({ ...acct, [a.qboId]: e.target.value })}
                  >
                    <option value="">Not mapped</option>
                    {usable.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.code} {x.fullName}
                      </option>
                    ))}
                  </NativeSelect>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {plan.mapping.taxCodes.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>QuickBooks tax code</TableHead>
                <TableHead>On</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Yedo Books tax code</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.mapping.taxCodes.map((t) => {
                const k = `${t.side}:${t.qboId}`;
                return (
                  <TableRow key={k} className={cn(!t.taxCodeId && !tax[k] && 'bg-warning/10')}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.side === 'sales' ? 'Sales' : 'Purchases'}</TableCell>
                    <TableCell className="tnum text-right">{t.rate}</TableCell>
                    <TableCell>
                      <NativeSelect
                        aria-label={`Ledger tax code for ${t.name} on ${t.side}`}
                        value={tax[k] ?? t.taxCodeId ?? ''}
                        onChange={(e) => setTax({ ...tax, [k]: e.target.value })}
                      >
                        <option value="">Not mapped</option>
                        {(codes.data ?? [])
                          .filter(
                            (c) => c.isActive && (c.appliesTo === 'both' || c.appliesTo === t.side),
                          )
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.code} · {c.name}
                              {c.effectiveTo ? ` (to ${formatDate(c.effectiveTo)})` : ''}
                            </option>
                          ))}
                      </NativeSelect>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
        {toCreate.length || itemsToCreate.length ? (
          <p className="text-sm text-muted-foreground">
            Added when the import starts:{' '}
            {[
              ...toCreate.map(
                (c) =>
                  `${c.name} (${c.kind}${c.currency !== plan.company.homeCurrency ? `, ${c.currency}` : ''})`,
              ),
              ...itemsToCreate.map((i) => i.name),
            ].join(', ')}
            . Customers, vendors and products with the same name in Yedo Books are used as they are.
          </p>
        ) : null}
        <div>
          <Button
            variant="outline"
            disabled={save.isPending || (!Object.keys(acct).length && !Object.keys(tax).length)}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save mapping and check again'}
          </Button>
        </div>
      </div>
    </details>
  );
}

const RESOLUTION_LABEL = {
  imported: 'Imported later',
  applied: 'Payment applied',
  reviewed: 'Reviewed',
} as const;

function Issues({ run }: { run: RunView }) {
  const errors = run.issues.filter((i) => i.severity === 'error');
  const warnings = run.issues.filter((i) => i.severity === 'warning');
  const open = (l: typeof errors) => l.filter((i) => !i.resolution).length;
  return (
    <details className="rounded-md border px-3 py-2" data-testid="qbo-import-issues">
      <summary className="cursor-pointer text-sm font-medium">
        Every problem and note: {errors.length} {errors.length === 1 ? 'problem' : 'problems'}
        {open(errors) !== errors.length ? ` (${open(errors)} open)` : ''}, {warnings.length}{' '}
        {warnings.length === 1 ? 'warning' : 'warnings'}
        {open(warnings) !== warnings.length ? ` (${open(warnings)} open)` : ''}
      </summary>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead>Transaction</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>What</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...errors, ...warnings].map((i, n) => (
            <TableRow key={n}>
              <TableCell>
                {i.resolution ? (
                  <StatusChip tone="success">{RESOLUTION_LABEL[i.resolution]}</StatusChip>
                ) : (
                  <StatusChip tone={i.severity === 'error' ? 'danger' : 'warning'}>
                    {i.severity === 'error' ? 'Not imported' : 'Note'}
                  </StatusChip>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {i.type ? `${i.type} ${i.number ?? `#${i.qboId}`}` : '—'}
              </TableCell>
              <TableCell className="tnum">{i.date ? formatDate(i.date) : ''}</TableCell>
              <TableCell className="text-sm">{i.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </details>
  );
}

function Verification({ id }: { id: string }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const v = useVerification(id, true);
  const refresh = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/qbo/history/${id}/verification`, { method: 'POST' }),
    onSuccess: (data) => qc.setQueryData(['qbo-history', orgId, id, 'verification'], data),
  });
  if (v.isPending) return <p className="text-sm text-muted-foreground">Loading the comparison…</p>;
  if (v.isError) return <ErrorBanner>{v.error.message}</ErrorBanner>;
  const data = v.data;
  return (
    <div className="flex flex-col gap-3" data-testid="qbo-import-verification">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">
          Compared with QuickBooks{' '}
          {data.differences ? (
            <StatusChip tone="warning">
              {data.differences} {data.differences === 1 ? 'difference' : 'differences'}
            </StatusChip>
          ) : (
            <StatusChip tone="success">No differences</StatusChip>
          )}
        </h3>
        <Button variant="outline" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Reading QuickBooks…' : 'Read QuickBooks again'}
        </Button>
      </div>
      <ErrorBanner>{refresh.error?.message}</ErrorBanner>
      {data.reports.map((r) => (
        <details
          key={`${r.report}-${r.asOf}`}
          open={r.differences > 0}
          className="rounded-md border px-3 py-2"
        >
          <summary className="cursor-pointer text-sm">
            {r.title} at {formatDate(r.asOf)}
            {r.differences
              ? ` · ${r.differences} ${r.differences === 1 ? 'difference' : 'differences'}`
              : ' · agrees'}
          </summary>
          <Table className="mt-2">
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead className="text-right">QuickBooks</TableHead>
                <TableHead className="text-right">Yedo Books</TableHead>
                <TableHead className="text-right">Difference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.lines.map((l) => {
                const differs = !isZero(l.difference);
                return (
                  <TableRow
                    key={l.key}
                    className={cn(differs && 'bg-warning/10')}
                    data-difference={differs || undefined}
                  >
                    <TableCell>{l.label}</TableCell>
                    <TableCell className="tnum text-right">{fmtAmount(l.qbo)}</TableCell>
                    <TableCell className="tnum text-right">
                      {l.ledger !== null && l.drill ? (
                        <DrillLink drill={l.drill as Drill} amount>
                          {fmtAmount(l.ledger)}
                        </DrillLink>
                      ) : (
                        fmtAmount(l.ledger)
                      )}
                    </TableCell>
                    <TableCell className="tnum text-right">
                      {differs ? (
                        <strong>{fmtAmount(l.difference)}</strong>
                      ) : (
                        <span className="text-muted-foreground">{fmtAmount(l.difference)}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </details>
      ))}
    </div>
  );
}
